/**
 * API Module
 * Single entry point for all API operations
 *
 * Usage:
 * ```ts
 * import { api } from "@/lib/api"
 *
 * // Use endpoints
 * const conversations = await api.workspaces.listConversations(token, workspaceId)
 * const object = await api.objects.getObject(token, objectId)
 *
 * // Use HTTP client directly
 * const response = await api.http.get('/custom-endpoint')
 * ```
 */

import * as objects from './endpoints/objects'
import * as chat from './endpoints/chat'
import * as search from './endpoints/search'
import * as agent from './endpoints/agent'
import * as user from './endpoints/user'
import * as spaces from './endpoints/spaces'
import * as workspaces from './endpoints/workspaces'
import * as assets from './endpoints/assets'
import * as entitlements from './endpoints/entitlements'
import { onboarding } from './endpoints/onboarding'
import { demoSeed } from './endpoints/demo-seed'
import { selfLetter } from './endpoints/self-letter'
import { http } from '@/lib/http'
import { env } from '@/lib/env'

/**
 * Centralized API module
 */
export const api = {
  /** Object operations */
  objects,
  /** Chat & streaming */
  chat,
  /** Search operations */
  search,
  /** Agent tool calls */
  agent,
  /** User & session */
  user,
  /** Onboarding state machine */
  onboarding,
  /** Demo-seed loader (synthetic onboarding data) */
  demoSeed,
  /** Self-letter (warm-letter onboarding page) */
  selfLetter,
  /** Asset search / listing / multipart upload */
  assets,
  /** Space management (members, invites, limits) */
  spaces,
  /** Per-workspace entitlement projection (ADR 042) */
  entitlements,
  /** Workspace, conversation, panel APIs */
  workspaces,
  /** Direct HTTP client access */
  http,
  /** Environment config */
  env,
}

/**
 * Re-export types for convenience
 */
export type {
  AnyObject,
  TaskObject,
  EventObject,
  ContactObject,
  NoteObject,
  PlaceObject,
  CreateObjectRequest,
  UpdateObjectRequest,
  PageThumbnailMetadata,
  PageOgImageMetadata,
} from './endpoints/objects'

export { ThumbnailUploadError } from './endpoints/objects'

export { StorageLimitExceededError } from './endpoints/assets'
export type { UploadedAsset, ImportUploadResponse } from './endpoints/assets'

export type {
  ChatEvent,
  ChatRequest,
} from './endpoints/chat'

export type {
  SearchResponse,
} from './endpoints/search'

export type {
  UserProfile,
  SessionStatusResponse,
} from './endpoints/user'

export type {
  ConversationLink,
  ConversationMessage,
  ConversationSession,
  ModuleSidebarPreference,
  WorkspaceModule,
  ConversationPanel,
} from './endpoints/workspaces'

/**
 * Re-export HTTP utilities
 */
export { HttpError, ContainerWarmingUpError } from '@/lib/http'
export type { HttpResponse } from '@/lib/http'
